import React from 'react';
import { motion } from 'framer-motion';
import { Button } from '../components/Button';
import { Section, Container, Badge } from '../components/UIComponents';
import { ChevronRight, Zap, Shield, Truck, Clock, Award, Users, CheckCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const HeroSection = () => {
  const navigate = useNavigate();

  return (
    <section className="bg-gradient-to-br from-slate-50 via-teal-50 to-blue-50 pt-8 md:pt-16 pb-16 md:pb-28">
      <Container>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="mb-6"
            >
              <Badge variant="primary">Trusted by 500+ Healthcare Professionals</Badge>
            </motion.div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 mb-6 leading-tight">
              Premium <span className="text-teal-600">Medical Equipment</span> for Healthcare Excellence
            </h1>

            <p className="text-lg md:text-xl text-slate-600 mb-8 leading-relaxed max-w-lg">
              Discover premium surgical equipment and medical supports trusted by hospitals, clinics, and healthcare professionals across India.
            </p>

            {/* Trust Badges */}
            <div className="grid grid-cols-2 gap-4 mb-8">
              <div className="flex items-center gap-3">
                <CheckCircle className="text-green-600" size={24} />
                <span className="text-sm font-semibold text-slate-700">ISO Certified</span>
              </div>
              <div className="flex items-center gap-3">
                <Truck className="text-teal-600" size={24} />
                <span className="text-sm font-semibold text-slate-700">Fast Delivery</span>
              </div>
              <div className="flex items-center gap-3">
                <Shield className="text-blue-600" size={24} />
                <span className="text-sm font-semibold text-slate-700">Quality Assured</span>
              </div>
              <div className="flex items-center gap-3">
                <Award className="text-amber-600" size={24} />
                <span className="text-sm font-semibold text-slate-700">Best Prices</span>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate('/products')}
                className="px-8 py-4 text-lg font-semibold rounded-lg bg-teal-600 hover:bg-teal-700 text-white transition-all duration-200 shadow-md"
              >
                Shop Now
              </motion.button>
              <Button
                size="lg"
                variant="outline"
                onClick={() => document.getElementById('categories')?.scrollIntoView({ behavior: 'smooth' })}
              >
                View Categories
              </Button>
            </div>
          </motion.div>

          {/* Right Image */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative flex items-center justify-center"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-teal-200 to-blue-200 rounded-2xl blur-3xl opacity-30"></div>
            <img 
              src="/healthpic.jpeg" 
              alt="Medical Equipment" 
              className="relative w-full max-w-md rounded-2xl shadow-2xl object-contain"
            />
          </motion.div>
        </div>
      </Container>
    </section>
  );
};

const CategoriesSection = ({ categories = [], products = [] }) => {
  const displayCategories = categories.slice(0, 6);

  if (displayCategories.length === 0) {
    return null; // Don't show section if no categories
  }

  // Function to count products in a category
  const getProductCount = (categoryName) => {
    return products.filter(p => p.category === categoryName).length;
  };

  return (
    <Section id="categories" title="Shop by Category" subtitle="Find exactly what you need with our comprehensive range of medical equipment">
      <Container>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
          {displayCategories.map((cat, i) => (
            <motion.div
              key={cat.id || i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.05 }}
              className="bg-white border border-slate-200 rounded-xl p-6 text-center hover:border-teal-300 hover:shadow-md transition-all duration-300 cursor-pointer group"
            >
              <div className="text-5xl mb-4 group-hover:scale-110 transition-transform duration-300">📦</div>
              <h3 className="font-bold text-slate-900 mb-2">{cat.name}</h3>
              <p className="text-sm text-slate-600">{getProductCount(cat.name)} Products</p>
            </motion.div>
          ))}
        </div>
      </Container>
    </Section>
  );
};

const FeaturedProductsSection = ({ products = [] }) => {
  // Use first 6 real products
  const displayProducts = products.slice(0, 6);

  if (displayProducts.length === 0) {
    return null; // Don't show section if no products
  }

  return (
    <Section title="Featured Products" subtitle="Handpicked premium medical equipment trusted by professionals" className="bg-slate-50">
      <Container>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {displayProducts.map((product, i) => (
            <motion.div
              key={product.id}
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="bg-white rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-shadow"
            >
              {/* Product Image */}
              <div className="bg-gradient-to-br from-slate-100 to-slate-200 h-48 md:h-40 flex items-center justify-center relative group overflow-hidden">
                {product.image ? (
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-contain group-hover:scale-110 transition-transform duration-300"
                  />
                ) : (
                  <div className="text-6xl opacity-80 group-hover:opacity-100 group-hover:scale-110 transition-all duration-300">📦</div>
                )}
                {product.discount > 0 && (
                  <div className="absolute top-3 right-3 bg-red-500 text-white px-3 py-1 rounded-full text-sm font-bold">
                    -{product.discount}%
                  </div>
                )}
              </div>

              {/* Product Info */}
              <div className="p-4">
                <p className="text-xs uppercase text-slate-500 font-semibold mb-1">{product.category || 'Medical Equipment'}</p>
                <h3 className="text-sm font-bold text-slate-900 mb-2 line-clamp-2">{product.name}</h3>
                <p className="text-xs text-slate-600 mb-3 line-clamp-2">{product.description || 'Premium medical equipment'}</p>
                
                {/* Price */}
                <div className="flex items-baseline gap-2 mb-3">
                  {(() => {
                    const productUsesPerSizePricing = product.usesPerSizePricing === true;
                    const isPerSizePricing = product.category?.toLowerCase() === 'tapes' || productUsesPerSizePricing;

                    if (isPerSizePricing && product.sizes && product.sizes.length > 0) {
                      const prices = product.sizes
                        .filter(s => s.price && s.price > 0)
                        .map(s => {
                          const discount = s.discount ?? 0;
                          const discountPercent = discount > 1 ? discount / 100 : discount;
                          return Math.round(s.price - (s.price * discountPercent));
                        });

                      if (prices.length > 0) {
                        const minPrice = Math.min(...prices);
                        const maxPrice = Math.max(...prices);
                        return (
                          <div className="flex flex-col gap-1">
                            <span className="text-lg font-bold text-teal-600">
                              {minPrice === maxPrice
                                ? `₹${minPrice.toLocaleString('en-IN')}`
                                : `₹${minPrice.toLocaleString('en-IN')} - ₹${maxPrice.toLocaleString('en-IN')}`}
                            </span>
                            <span className="text-xs text-slate-500 font-semibold">Select Size to View Price</span>
                          </div>
                        );
                      }
                    }

                    if (product.discount > 0) {
                      return (
                        <>
                          <span className="text-lg font-bold text-teal-600">₹{Math.round(product.price - (product.price * product.discount / 100)).toLocaleString('en-IN')}</span>
                          <span className="text-sm text-slate-500 line-through">₹{product.price.toLocaleString('en-IN')}</span>
                        </>
                      );
                    }
                    return <span className="text-lg font-bold text-teal-600">₹{product.price.toLocaleString('en-IN')}</span>;
                  })()}
                </div>

                {/* Select Size / In Stock */}
                <div className="flex items-center gap-2 mb-3">
                  {product.sizes && product.sizes.length > 0 ? (
                    <span className="text-teal-600 text-sm font-semibold">📏 Select Size</span>
                  ) : product.stock > 0 ? (
                    <span className="text-green-600 text-sm font-semibold">✓ In Stock</span>
                  ) : (
                    <span className="text-red-600 text-sm font-semibold">Out of Stock</span>
                  )}
                </div>

              </div>
            </motion.div>
          ))}
        </div>
      </Container>
    </Section>
  );
};

const WhyChooseUsSection = () => {
  const features = [
    {
      icon: Zap,
      title: 'Fast Shipping',
      description: 'Get your medical equipment delivered within 24-48 hours across India',
    },
    {
      icon: Shield,
      title: 'Quality Assured',
      description: 'All products are ISO certified and tested for maximum safety',
    },
    {
      icon: Award,
      title: 'Best Prices',
      description: 'Competitive pricing without compromising on quality',
    },
    {
      icon: Users,
      title: 'Expert Support',
      description: 'Dedicated support team to help you choose the right products',
    },
    {
      icon: Clock,
      title: '24/7 Available',
      description: 'Round-the-clock customer support for your peace of mind',
    },
    {
      icon: CheckCircle,
      title: 'Money Back',
      description: '30-day money back guarantee if not satisfied',
    },
  ];

  return (
    <Section title="Why Choose Us" subtitle="Premium service, professional products, trusted by thousands">
      <Container>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature, i) => {
            const Icon = feature.icon;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                className="text-center"
              >
                <motion.div
                  whileHover={{ scale: 1.1, rotate: 5 }}
                  className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-teal-100 to-blue-100 rounded-full mb-4"
                >
                  <Icon className="text-teal-600" size={32} />
                </motion.div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">{feature.title}</h3>
                <p className="text-slate-600">{feature.description}</p>
              </motion.div>
            );
          })}
        </div>
      </Container>
    </Section>
  );
};

const TestimonialsSection = () => {
  const testimonials = [
    {
      name: 'Keerthi Boyapati',
      role: 'Customer',
      location: 'Satisfied Customer',
      text: 'I needed a adhesive bandage as I got a small surgery and they were very helpful and kind to deliver it to my home. Thank you so much! Very reasonable prices.',
      avatar: '😊',
    },
    {
      name: 'Thousiff Muhammed',
      role: 'Customer',
      location: 'Satisfied Customer',
      text: 'Good services. Specially for senior people\'s needs. All the best!',
      avatar: '👍',
    },
  ];

  return (
    <Section title="What Customers Say" subtitle="Join thousands of satisfied customers across India" className="bg-slate-50">
      <Container>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {testimonials.map((testimonial, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="bg-white rounded-xl p-8 shadow-md hover:shadow-lg transition-shadow"
            >
              {/* Text */}
              <p className="text-slate-700 text-lg mb-6 leading-relaxed italic">
                "{testimonial.text}"
              </p>

              {/* Author */}
              <div className="flex items-center gap-4">
                <div className="text-4xl">{testimonial.avatar}</div>
                <div>
                  <p className="font-bold text-slate-900">{testimonial.name}</p>
                  <p className="text-sm text-slate-600">{testimonial.role}</p>
                  <p className="text-xs text-slate-500">{testimonial.location}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </Container>
    </Section>
  );
};

const CTASection = () => {
  const navigate = useNavigate();

  return (
    <Section className="bg-gradient-to-r from-teal-600 to-blue-600 text-white">
      <Container>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center py-8"
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Ready to Upgrade Your Medical Equipment?
          </h2>
          <p className="text-lg text-teal-100 mb-8 max-w-2xl mx-auto">
            Browse our complete range of premium surgical equipment and get fast delivery across India. Join 500+ satisfied healthcare professionals.
          </p>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => navigate('/products')}
            className="px-8 py-4 text-lg font-semibold rounded-lg bg-white text-teal-600 hover:bg-slate-100 transition-all duration-200 shadow-lg flex items-center gap-2 mx-auto"
          >
            Start Shopping
            <ChevronRight size={20} />
          </motion.button>
        </motion.div>
      </Container>
    </Section>
  );
};

const Footer = () => {
  return (
    <footer className="bg-slate-900 text-slate-300 py-12 md:py-16">
      <Container>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8 pb-8 border-b border-slate-700">
          {/* Brand */}
          <div>
            <h3 className="text-white font-bold text-lg mb-4">HCS Enterprises</h3>
            <p className="text-sm text-slate-400">Premium surgical equipment and medical supports trusted by healthcare professionals across India.</p>
            {/* Social Media */}
            <div className="flex gap-4 mt-4">
              <a href="https://wa.me/917680944286" target="_blank" rel="noopener noreferrer" className="transition-transform hover:scale-125" title="WhatsApp">
                <svg className="w-7 h-7" viewBox="0 0 24 24" fill="#25D366">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.67-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421-7.403h-.004a6.716 6.716 0 00-6.708 6.71c0 1.318.345 2.602 1.002 3.714l-1.064 3.891 3.985-1.046a6.704 6.704 0 003.785 1.089h.005c3.703 0 6.71-3.008 6.71-6.71a6.706 6.706 0 00-6.711-6.71M12.051 2.001C6.581 2.001 2.2 6.382 2.2 11.852c0 2.096.547 4.142 1.588 5.945L2 22.1l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c5.471 0 9.852-4.381 9.852-9.851a9.844 9.844 0 00-9.848-9.852" />
                </svg>
              </a>
              <a href="https://instagram.com/hcs_enterprises" target="_blank" rel="noopener noreferrer" className="transition-transform hover:scale-125 rounded p-px bg-gradient-to-br from-yellow-400 via-pink-500 to-purple-600" title="Instagram" style={{ width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zM5.838 12a6.162 6.162 0 1112.324 0 6.162 6.162 0 01-12.324 0zM12 16a4 4 0 110-8 4 4 0 010 8zm4.965-10.322a1.44 1.44 0 11-2.881 0 1.44 1.44 0 012.881 0z" />
                </svg>
              </a>
            </div>
          </div>

          {/* Company */}
          <div>
            <h4 className="text-white font-semibold mb-4">Company</h4>
            <ul className="space-y-2 text-sm">
              <li><a href="/about" className="hover:text-teal-400 transition-colors">About Us</a></li>
              <li><a href="/contact" className="hover:text-teal-400 transition-colors">Contact</a></li>
              <li><a href="#" className="hover:text-teal-400 transition-colors">Blog</a></li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="text-white font-semibold mb-4">Legal</h4>
            <ul className="space-y-2 text-sm">
              <li><a href="#" className="hover:text-teal-400 transition-colors">Privacy Policy</a></li>
              <li><a href="#" className="hover:text-teal-400 transition-colors">Terms & Conditions</a></li>
              <li><a href="#" className="hover:text-teal-400 transition-colors">Refund Policy</a></li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-white font-semibold mb-4">Contact</h4>
            <ul className="space-y-2 text-sm">
              <li className="text-slate-400">📧 contact@hcsenterprises.com</li>
              <li className="text-slate-400">📞 +91 7680944286</li>
              <li className="text-slate-400">📍 12-2-460/1/1,2 Mahek's Vijaya Vihar, Gudi Malkapur Road, Hyderabad, Telangana 500006</li>
            </ul>
          </div>
        </div>

        {/* Bottom */}
        <div className="flex flex-col md:flex-row justify-between items-center text-sm text-slate-500">
          <p>&copy; 2026 HCS Enterprises. All rights reserved.</p>
        </div>
      </Container>
    </footer>
  );
};

export default function PremiumHome({ products = [], categories = [] }) {
  return (
    <div className="w-full bg-white">
      <HeroSection />
      <CategoriesSection categories={categories} products={products} />
      <FeaturedProductsSection products={products} />
      <WhyChooseUsSection />
      <TestimonialsSection />
      <CTASection />
      <Footer />
    </div>
  );
}
